import React from 'react';

interface FormField {
  name: string;
  label: string;
  type: 'text' | 'textarea' | 'date' | 'select' | 'checkbox' | 'number' | 'email';
  required?: boolean;
  placeholder?: string;
  options?: Array<{ value: string; label: string }>;
  helpText?: string;
}

interface TransitionFormRendererProps {
  formDefinition: Record<string, unknown>;
  value: Record<string, unknown>;
  onChange: (data: Record<string, unknown>) => void;
  errors?: Record<string, string>;
}

function extractFields(formDefinition: Record<string, unknown>): FormField[] {
  const fields = formDefinition.fields;
  if (Array.isArray(fields)) return fields as FormField[];
  if (typeof formDefinition === 'object' && formDefinition.properties) {
    const props = formDefinition.properties as Record<string, Record<string, unknown>>;
    const required = Array.isArray(formDefinition.required) ? formDefinition.required as string[] : [];
    return Object.entries(props).map(([name, schema]) => ({
      name,
      label: (schema.title as string) || name,
      type: (schema.type as FormField['type']) || 'text',
      required: required.includes(name),
      placeholder: schema.placeholder as string | undefined,
      options: schema.enum
        ? (schema.enum as string[]).map(v => ({ value: v, label: v }))
        : undefined,
      helpText: schema.description as string | undefined,
    }));
  }
  return [];
}

const TransitionFormRenderer: React.FC<TransitionFormRendererProps> = ({
  formDefinition,
  value,
  onChange,
  errors,
}) => {
  const fields = extractFields(formDefinition);

  if (fields.length === 0) return null;

  const handleChange = (name: string, fieldValue: unknown) => {
    onChange({ ...value, [name]: fieldValue });
  };

  const inputClass = 'w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-lex-accent';
  const errorInputClass = 'border-rose-300 dark:border-rose-600 focus:ring-rose-500';
  const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';

  return (
    <div className="space-y-4">
      {fields.map((field) => {
        const fieldError = errors?.[field.name];
        const fieldInputClass = fieldError ? `${inputClass} ${errorInputClass}` : inputClass;

        return (
          <div key={field.name}>
            <label className={labelClass}>
              {field.label}
              {field.required && <span className="text-rose-500 ml-0.5">*</span>}
            </label>

            {field.type === 'textarea' ? (
              <textarea
                className={fieldInputClass}
                rows={3}
                placeholder={field.placeholder}
                value={(value[field.name] as string) || ''}
                onChange={(e) => handleChange(field.name, e.target.value)}
              />
            ) : field.type === 'select' && field.options ? (
              <select
                className={fieldInputClass}
                value={(value[field.name] as string) || ''}
                onChange={(e) => handleChange(field.name, e.target.value)}
              >
                <option value="">Select...</option>
                {field.options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            ) : field.type === 'checkbox' ? (
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={!!value[field.name]}
                  onChange={(e) => handleChange(field.name, e.target.checked)}
                  className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent dark:border-gray-600"
                />
                {field.placeholder || field.label}
              </label>
            ) : (
              <input
                type={field.type}
                className={fieldInputClass}
                placeholder={field.placeholder}
                value={(value[field.name] as string) || ''}
                onChange={(e) => handleChange(field.name, e.target.value)}
              />
            )}

            {field.helpText && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{field.helpText}</p>
            )}
            {fieldError && (
              <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">{fieldError}</p>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default TransitionFormRenderer;
